
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { logout, tokenReceived } from '../features/auth/authSlice';
import { refrescarSesion } from './refrescarSesion';
import { RootState } from '../redux/store';
import { settings } from '../settings';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Organism } from '../pages/Organismos/hooks/useAddOrganism';

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});

export interface CreateOrganismRequest {
    razonSocial: string;
    // cuit: string;
    polizaSRT: number;
    idCIIU: number;
    idOrganismoCentral: number;
    nroContrato: string;
    organismos: { razonSocial: string; cuitOrganismo: string }[];
}

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const refreshResult = await refrescarSesion(baseQuery, api, extraOptions);
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const organismsApi = createApi({
    reducerPath: 'organisms',
    tagTypes: ['Organismos', 'OrganismoById'],
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        createOrganism: builder.mutation({
            query: (body: any) => ({
                url: 'organismos',
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['Organismos']
        }),
        editOrganism: builder.mutation({
            query: ({ body, organismId }: any) => ({
                url: `organismos/${organismId}`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: ['Organismos']
        }),
        getOrganisms: builder.query({
            query: (params) => ({
                url: `organismos`,
                params
            }),
            keepUnusedDataFor: 5,
            providesTags: ['Organismos']
        }),
        /**
         * Trae todos los organismos que cumplen los filtros, sin un `take` fijo.
         * Primero pide uno solo para conocer el total filtrado (`count`; `totalCount`
         * cuenta la tabla entera sin filtros) y, si hay más, los pide todos.
         * Los dos requests viven en el mismo `queryFn`, así que `isFetching` no se
         * apaga entre uno y otro y nunca se expone la lista parcial.
         */
        getTodosLosOrganismos: builder.query<any, Record<string, any>>({
            async queryFn(filtros, _api, _extraOptions, fetchWithBQ) {
                const primera = await fetchWithBQ({
                    url: 'organismos',
                    params: { ...filtros, skip: 0, take: 1 }
                });
                if (primera.error) return { error: primera.error };

                const pagina = (primera.data as any)?.data;
                const count = pagina?.count ?? 0;
                if (count <= (pagina?.data?.length ?? 0)) return { data: primera.data };

                const todos = await fetchWithBQ({
                    url: 'organismos',
                    params: { ...filtros, skip: 0, take: count }
                });
                return todos.error ? { error: todos.error } : { data: todos.data };
            },
            keepUnusedDataFor: 5,
            providesTags: ['Organismos']
        }),
        deregisterOrganism: builder.mutation({
            query: ({ body, organismId }: any) => ({
                url: `organismos/${organismId}/estado`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: ['Organismos']
        }),
        getOrganismContractFile: builder.query({
            query: ({ idOrganismo }) => ({
                url: `organismos/${idOrganismo}/archivo`
            }),
            keepUnusedDataFor: 5,
            providesTags: ['Organismos']
        }),
        getCentralOrganisms: builder.query({
            query: ({ idOrganizacion }) => ({
                url: 'organismos/organismos-central',
                method: 'GET',
                params:
                    idOrganizacion && idOrganizacion?.id
                        ? { idOrganizacion: idOrganizacion?.id }
                        : idOrganizacion
                        ? { idOrganizacion }
                        : undefined
            }),
            keepUnusedDataFor: 5,
            providesTags: ['Organismos']
        }),
        getCentralOrganismsByContrato: builder.query({
            query: ({ idOrganizacion }: { idOrganizacion: number }) => ({
                url: `organismos/organismo-central/${idOrganizacion}`,
                method: 'GET'
            }),
            keepUnusedDataFor: 5,
            providesTags: ['Organismos']
        })
    })
});

export const {
    useCreateOrganismMutation,
    useEditOrganismMutation,
    useDeregisterOrganismMutation,
    useGetOrganismsQuery,
    useGetTodosLosOrganismosQuery,
    useLazyGetOrganismContractFileQuery,
    useGetCentralOrganismsQuery,
    useGetCentralOrganismsByContratoQuery
} = organismsApi;
